import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useApp } from "../context/AppContext";
import legacyBlogs from "../data/blogs";
import PageSeo from "../components/PageSeo";
import "./Blogs.css";

function formatDate(value) {
  if (!value) return "Recently";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  return String(value);
}

function getReadTime(content = "", fallback = 1) {
  const wordCount = String(content || "")
    .replace(/[#*_`>\[\]\(\)\-|]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.ceil(wordCount / 200)) || fallback;
}

function getInitials(name = "") {
  if (!name) return "MH";
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "MH"
  );
}

function escapeRegExp(value = "") {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightText(text, query) {
  const source = String(text || "");
  const term = String(query || "").trim();
  if (!term) return source;

  const parts = source.split(new RegExp(`(${escapeRegExp(term)})`, "ig"));
  return parts.map((part, index) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <span key={`${part}-${index}`} className="blogs-highlight">
        {part}
      </span>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  );
}

function normalizeCommunityBlog(blog) {
  return {
    ...blog,
    cover_image: blog.cover_image || null,
    category: blog.category || "General",
    read_time: Number(blog.read_time || 0) || getReadTime(blog.excerpt || blog.content || blog.title),
    views: Number(blog.views || 0),
    likes: Number(blog.likes || 0),
    published_at: blog.published_at || blog.created_at || null,
    created_at: blog.created_at || blog.published_at || null,
    source: "community",
    author: blog.author || null,
  };
}

function normalizeLegacyBlog(blog) {
  const content = blog.content || "";

  return {
    id: `legacy-${blog.slug}`,
    title: blog.title,
    slug: blog.slug,
    excerpt: blog.description || content.trim().slice(0, 180),
    content,
    cover_image: blog.image || null,
    category: "MotoHub Editorial",
    tags: [],
    read_time: getReadTime(content, 2),
    views: 0,
    likes: 0,
    created_at: blog.date || null,
    published_at: blog.date || null,
    author: {
      id: `legacy-author-${blog.slug}`,
      name: blog.author || "MotoHub India Editorial",
      username: blog.slug,
      avatar_url: null,
      city: "India",
    },
    source: "legacy",
  };
}

function sortByPublishedAtDesc(a, b) {
  const aTime = Date.parse(a.published_at || a.created_at || 0) || 0;
  const bTime = Date.parse(b.published_at || b.created_at || 0) || 0;
  return bTime - aTime;
}

const CATEGORIES = [
  "All",
  "Riding Story",
  "Route Review",
  "Bike Review",
  "Tips & Tricks",
  "Maintenance",
  "Buying Guide",
  "Comparison",
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "most_viewed", label: "Most Viewed" },
  { value: "most_liked", label: "Most Liked" },
  { value: "trending", label: "Trending" },
];

export default function Blogs() {
  const navigate = useNavigate();
  const { user } = useApp();
  const [communityBlogs, setCommunityBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeSort, setActiveSort] = useState("newest");
  const [showMyBlogsOnly, setShowMyBlogsOnly] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const baseUrl = (typeof window !== "undefined" && window.location.origin) || "https://motohubindia.com";

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 180);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getShareLink = (slug) => `${baseUrl}/blog/${slug}`;

  const handleCopyLink = async (event, slug) => {
    event.stopPropagation();
    const link = getShareLink(slug);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const temp = document.createElement("input");
        temp.value = link;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand("copy");
        document.body.removeChild(temp);
      }
      setShareMessage("Blog link copied");
      setTimeout(() => setShareMessage(""), 1800);
    } catch {
      setShareMessage("Unable to copy link");
      setTimeout(() => setShareMessage(""), 1800);
    }
  };

  const handleShareWhatsApp = (event, blog) => {
    event.stopPropagation();
    const link = getShareLink(blog.slug);
    const text = `${blog.title} - ${link}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleShareInstagram = (event, blog) => {
    event.stopPropagation();
    const link = getShareLink(blog.slug);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(link).catch(() => {});
    }
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    setShareMessage("Link copied, paste it in Instagram");
    setTimeout(() => setShareMessage(""), 2200);
  };

  useEffect(() => {
    let active = true;

    const fetchWithAuthorRelation = async () =>
      supabase
        .from("blog_posts")
        .select(`
          id, title, slug, excerpt, cover_image,
          category, tags, read_time, views, likes,
          created_at, published_at,
          author:profiles(name, username, avatar_url, city)
        `)
        .eq("status", "approved")
        .order("published_at", { ascending: false });

    const fetchWithoutAuthorRelation = async () =>
      supabase
        .from("blog_posts")
        .select(`
          id, title, slug, excerpt, cover_image,
          category, tags, read_time, views, likes,
          created_at, published_at, author_id
        `)
        .eq("status", "approved")
        .order("published_at", { ascending: false });

    const loadBlogs = async () => {
      setLoading(true);

      let response = await fetchWithAuthorRelation();
      if (response.error) {
        response = await fetchWithoutAuthorRelation();
      }

      if (!active) return;

      if (response.error) {
        console.error("Failed to load blog posts", response.error);
        setCommunityBlogs([]);
        setLoading(false);
        return;
      }

      const rows = (response.data || []).map((blog) =>
        normalizeCommunityBlog({
          ...blog,
          author:
            blog.author ||
            (blog.author_id
              ? {
                  id: blog.author_id,
                  name: "MotoHub Rider",
                  username: "",
                  avatar_url: null,
                  city: "India",
                }
              : null),
        })
      );

      setCommunityBlogs(rows);
      setLoading(false);
    };

    loadBlogs();

    return () => {
      active = false;
    };
  }, []);

  const legacyBlogCards = useMemo(() => legacyBlogs.map(normalizeLegacyBlog), []);

  const blogs = useMemo(() => {
    const merged = [...communityBlogs, ...legacyBlogCards];
    const deduped = new Map();

    merged.forEach((blog) => {
      if (!blog?.slug) return;
      const existing = deduped.get(blog.slug);
      if (!existing || blog.source === "community") {
        deduped.set(blog.slug, blog);
      }
    });

    return Array.from(deduped.values()).sort(sortByPublishedAtDesc);
  }, [communityBlogs, legacyBlogCards]);

  const filteredBlogs = useMemo(() => {
    const term = debouncedQuery.toLowerCase();
    const list = blogs.filter((blog) => {
      const category = String(blog.category || "").toLowerCase();
      const active = activeCategory.toLowerCase();
      const haystack = [
        blog.title,
        blog.excerpt,
        blog.content,
        blog.category,
        blog.author?.name,
        blog.author?.username,
        ...(Array.isArray(blog.tags) ? blog.tags : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (term && !haystack.includes(term)) return false;
      if (activeCategory === "All") return true;
      if (category.includes(active)) return true;
      if (activeCategory === "Riding Story") return category.includes("story");
      if (activeCategory === "Route Review") return category.includes("route");
      if (activeCategory === "Bike Review") return category.includes("review");
      if (activeCategory === "Tips & Tricks") return category.includes("tips") || category.includes("trick");
      if (activeCategory === "Maintenance") return category.includes("maintenance");
      if (activeCategory === "Buying Guide") return category.includes("guide") || category.includes("buying");
      if (activeCategory === "Comparison") return category.includes("comparison");
      return true;
    });

    if (!showMyBlogsOnly || !user?.id) return list;

    return list.filter((blog) => {
      if (blog.source !== "community") return false;
      return blog.author?.id === user.id || blog.author_id === user.id;
    });
  }, [blogs, activeCategory, debouncedQuery, showMyBlogsOnly, user?.id]);

  const sortedBlogs = useMemo(() => {
    const list = [...filteredBlogs];
    if (activeSort === "oldest") {
      return list.sort((a, b) => {
        const aTime = Date.parse(a.published_at || a.created_at || 0) || 0;
        const bTime = Date.parse(b.published_at || b.created_at || 0) || 0;
        return aTime - bTime;
      });
    }

    if (activeSort === "most_viewed") {
      return list.sort((a, b) => Number(b.views || 0) - Number(a.views || 0));
    }

    if (activeSort === "most_liked") {
      return list.sort((a, b) => Number(b.likes || 0) - Number(a.likes || 0));
    }

    if (activeSort === "trending") {
      return list.sort((a, b) => {
        const aScore = Number(a.likes || 0) * 3 + Number(a.views || 0);
        const bScore = Number(b.likes || 0) * 3 + Number(b.views || 0);
        return bScore - aScore;
      });
    }

    return list.sort((a, b) => {
      const aTime = Date.parse(a.published_at || a.created_at || 0) || 0;
      const bTime = Date.parse(b.published_at || b.created_at || 0) || 0;
      return bTime - aTime;
    });
  }, [filteredBlogs, activeSort]);

  const uniqueAuthors = useMemo(() => {
    return new Set(
      blogs.map((blog) => blog.author?.username || blog.author?.name || blog.author?.id).filter(Boolean)
    ).size;
  }, [blogs]);

  const totalViews = useMemo(() => {
    return blogs.reduce((sum, blog) => sum + Number(blog.views || 0), 0);
  }, [blogs]);

  return (
    <>
      <PageSeo
        title="MotoHub India Blogs | Rider Stories, Tips and Guides"
        description="Read community riding stories, route reviews, bike tips, and motorcycle guides from Indian riders."
        keywords={[
          "MotoHub India blogs",
          "rider stories India",
          "bike reviews India",
          "motorcycle tips India",
        ]}
        path="/blogs"
        image="/og-image.jpg"
      />

      <div className="blogs-page">
        <section className="blogs-hero">
          <div className="blogs-shell">
            <p className="blogs-breadcrumb">MotoHub India > Community Blogs</p>
            <h1 className="blogs-title">Rider Stories and Bike Guides</h1>
            <p className="blogs-subtitle">
              Community-written guides, reviews, and riding stories from bikers across India.
            </p>

            <div className="blogs-hero-actions">
              {user ? (
                <button
                  onClick={() => navigate("/blog/write")}
                  className="blogs-primary-btn"
                  type="button"
                >
                  Write a Blog
                </button>
              ) : (
                <button
                  onClick={() => navigate("/login")}
                  className="blogs-primary-btn"
                  type="button"
                >
                  Login to Write
                </button>
              )}
            </div>

            {!user ? (
              <div className="blogs-login-banner">
                <div>
                  <p className="blogs-login-title">Got a riding story to share?</p>
                  <p className="blogs-login-subtitle">
                    Login to publish your route, review, or riding tips for the community.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/login")}
                  className="blogs-secondary-btn"
                  type="button"
                >
                  Login
                </button>
              </div>
            ) : null}
          </div>
        </section>

        <section className="blogs-stats-wrap">
          <div className="blogs-shell blogs-stats">
            <div className="blogs-stat-card">
              <p className="blogs-stat-value">{blogs.length}</p>
              <p className="blogs-stat-label">Total Blogs</p>
            </div>
            <div className="blogs-stat-card">
              <p className="blogs-stat-value">{uniqueAuthors}</p>
              <p className="blogs-stat-label">Rider Authors</p>
            </div>
            <div className="blogs-stat-card">
              <p className="blogs-stat-value">{totalViews}</p>
              <p className="blogs-stat-label">Total Views</p>
            </div>
          </div>
        </section>

        <section className="blogs-filters-wrap">
          <div className="blogs-shell blogs-filters">
            {CATEGORIES.map((category) => {
              const active = activeCategory === category;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`blogs-pill ${active ? "blogs-pill-active" : ""}`}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </section>

        <section className="blogs-grid-wrap">
          <div className="blogs-shell">
            <div className="blogs-controls-sticky">
              {shareMessage ? <div className="blogs-share-toast">{shareMessage}</div> : null}
              <div className="blogs-sort-row">
                <div className="blogs-sort-left">
                  <p className="blogs-sort-label">Sort by</p>
                  {user ? (
                    <button
                      type="button"
                      onClick={() => setShowMyBlogsOnly((prev) => !prev)}
                      className={`blogs-toggle-btn ${showMyBlogsOnly ? "blogs-toggle-btn-active" : ""}`}
                    >
                      {showMyBlogsOnly ? "Showing My Blogs" : "My Blogs"}
                    </button>
                  ) : null}
                </div>
                <div className="blogs-sort-pills">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setActiveSort(option.value)}
                      className={`blogs-sort-pill ${activeSort === option.value ? "blogs-sort-pill-active" : ""}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="blogs-searchbox">
                <span>Search blogs</span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search title, author, topic, or keywords..."
                  aria-label="Search blogs"
                />
              </label>
            </div>
            {loading ? (
              <div className="blogs-state-box">Loading blogs...</div>
            ) : sortedBlogs.length === 0 ? (
              <div className="blogs-state-box">
                <p className="blogs-state-title">
                  {searchQuery.trim()
                    ? "No blogs found for your search"
                    : "No blogs found for this category"}
                </p>
                <p className="blogs-state-sub">
                  {searchQuery.trim()
                    ? "Try a different keyword, author name, or topic."
                    : "Try another category or publish a new story."}
                </p>
                {user ? (
                  <button
                    onClick={() => navigate("/blog/write")}
                    className="blogs-primary-btn"
                    type="button"
                  >
                    Write First Blog
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="blogs-grid">
                {sortedBlogs.map((blog) => (
                  <article
                    key={blog.id}
                    onClick={() => navigate(`/blog/${blog.slug}`)}
                    className="blogs-card"
                  >
                    <div className="blogs-card-media">
                      {blog.cover_image ? (
                        <img
                          src={blog.cover_image}
                          alt={blog.title}
                          className="blogs-card-image"
                          loading="lazy"
                        />
                      ) : (
                        <div className="blogs-card-placeholder">
                          <span>MotoHub</span>
                        </div>
                      )}
                      <span className="blogs-card-category">{blog.category || "General"}</span>
                    </div>

                    <div className="blogs-card-body">
                      <h3 className="blogs-card-title">{highlightText(blog.title, searchQuery)}</h3>
                      <p className="blogs-card-excerpt">
                        {highlightText(
                          blog.excerpt || "Read the full story for practical tips, reviews, and riding insights.",
                          searchQuery
                        )}
                      </p>

                      <div className="blogs-card-author-row">
                        <div className="blogs-card-avatar">
                          {getInitials(blog.author?.name || blog.author || "MotoHub")}
                        </div>
                        <div className="blogs-card-author-meta">
                        <p className="blogs-card-author-name">
                            {highlightText(blog.author?.name || blog.author || "MotoHub Rider", searchQuery)}
                          </p>
                          <p className="blogs-card-author-city">{blog.author?.city || "India"}</p>
                        </div>
                      </div>

                      <div className="blogs-card-bottom">
                        <span>{blog.read_time || 1} min read</span>
                        <span>{Number(blog.views || 0).toLocaleString("en-IN")} views</span>
                      </div>
                      <p className="blogs-card-date">{formatDate(blog.published_at || blog.created_at)}</p>

                      <div className="blogs-card-share-row">
                        <button
                          type="button"
                          onClick={(event) => handleShareWhatsApp(event, blog)}
                          className="blogs-share-btn"
                        >
                          WhatsApp
                        </button>
                        <button
                          type="button"
                          onClick={(event) => handleShareInstagram(event, blog)}
                          className="blogs-share-btn"
                        >
                          Instagram
                        </button>
                        <button
                          type="button"
                          onClick={(event) => handleCopyLink(event, blog.slug)}
                          className="blogs-share-btn"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}
